// Custom hook for listening to language changes
// Provides reactive language state that updates when language changes

import { useState, useEffect } from 'react';
import { getCurrentLanguage, getLanguageCode } from '../../services/i18n';

/**
 * Hook that provides the current language and updates reactively when language changes
 * @returns The current language code (e.g., 'en', 'fr', 'ar')
 */
export const useLanguage = (): string => {
  const [currentLang, setCurrentLang] = useState<string>(getCurrentLanguage());

  useEffect(() => {
    // Initial language
    setCurrentLang(getCurrentLanguage());

    // Listen for language change events
    const handleLanguageChange = (event: CustomEvent<string>) => {
      const newLang = event.detail || getCurrentLanguage();
      setCurrentLang(newLang);
    };

    // Listen for custom languageChange event
    window.addEventListener('languageChange', handleLanguageChange as EventListener);

    // Also listen for storage changes (in case language is changed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'orca_ui_language_code') {
        const newLang = e.newValue || getCurrentLanguage();
        setCurrentLang(newLang);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Poll for language changes (fallback for components that don't use events)
    const interval = setInterval(() => {
      const current = getCurrentLanguage();
      if (current !== currentLang) {
        setCurrentLang(current);
      }
    }, 500);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentLang]);

  return currentLang;
};

/**
 * Hook that provides the current language name (e.g., 'English', 'Français')
 * @returns The current language name
 */
export const useLanguageName = (): string => {
  const langCode = useLanguage();
  const [langName, setLangName] = useState<string>('English');

  useEffect(() => {
    // Map language code to name
    const langMap: Record<string, string> = {
      'en': 'English',
      'fr': 'Français',
      'ar': 'العربية',
      'darija': 'Darija',
      'de': 'Deutsch',
      'es': 'Español'
    };
    setLangName(langMap[langCode] || 'English');
  }, [langCode]);

  return langName;
};

/**
 * Hook that forces a component to re-render when language changes
 * Useful for components that use t() function directly
 * @returns A force update function
 */
export const useLanguageUpdate = (): () => void => {
  const [, setUpdate] = useState(0);
  const currentLang = useLanguage();

  useEffect(() => {
    // Force update when language changes
    setUpdate(prev => prev + 1);
  }, [currentLang]);

  return () => setUpdate(prev => prev + 1);
};

